import { useState } from "react";
import DOMPurify from "dompurify";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, RotateCcw, History, ChevronDown, ChevronRight, User, Clock } from "lucide-react";

export const ArticleVersionHistory = ({
  open,
  onOpenChange,
  versions,
  loading,
  canEdit,
  restoring,
  onRestore,
}) => {
  const [expanded, setExpanded] = useState(null);

  const formatDate = (d) => (d ? new Date(d).toLocaleString("de-DE") : "");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px] max-h-[85vh] overflow-y-auto" data-testid="version-history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-5 h-5" />
            Versionsverlauf
          </DialogTitle>
          <DialogDescription>
            Frühere Versionen dieses Artikels ansehen und bei Bedarf wiederherstellen.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : versions.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground">
            Noch keine früheren Versionen vorhanden. Versionen entstehen automatisch bei jeder Bearbeitung.
          </div>
        ) : (
          <div className="space-y-2">
            {versions.map((v) => (
              <div key={v.id} className="rounded-lg border bg-card" data-testid={`version-item-${v.version}`}>
                <div className="flex items-center justify-between gap-2 p-3">
                  <button
                    type="button"
                    className="flex items-center gap-2 text-left flex-1 min-w-0"
                    onClick={() => setExpanded(expanded === v.id ? null : v.id)}
                    data-testid={`version-toggle-${v.version}`}
                  >
                    {expanded === v.id ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
                    <Badge variant="secondary">v{v.version}</Badge>
                    <span className="font-medium truncate">{v.title}</span>
                  </button>
                  {canEdit && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onRestore(v.version)}
                      disabled={restoring === v.version}
                      data-testid={`restore-version-${v.version}`}
                    >
                      {restoring === v.version ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <RotateCcw className="w-4 h-4 mr-1" />
                          Wiederherstellen
                        </>
                      )}
                    </Button>
                  )}
                </div>
                <div className="flex items-center gap-4 px-3 pb-2 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><User className="w-3 h-3" />{v.editor_name || "Unbekannt"}</span>
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{formatDate(v.created_at)}</span>
                  <Badge variant="outline" className="text-[10px]">{v.category}</Badge>
                </div>
                {expanded === v.id && (
                  <div
                    className="border-t p-3 prose prose-sm max-w-none bg-muted/30"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(v.content) }}
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
